import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import { ethers } from 'ethers6';
import { useAccount, useBalance } from 'wagmi';
import { executeWithRpcFailover } from '../../../utils/rpcProvider';
import { TokenData } from '../../../types';

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
`;

const ModalContent = styled.div`
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  backdrop-filter: blur(8px);
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  text-align: center;
`;

const SectionLabel = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AmountRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const AmountInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  padding: 12px 16px;
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const TokenDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  border-radius: 8px;
  padding: 16px 20px;
  min-width: 120px;
`;

const TokenLogo = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const TokenInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const TokenSymbol = styled.div`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
`;

const TokenName = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
`;

const BalanceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const BalanceText = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
`;

const PercentButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 16px;
`;

const PercentButton = styled.button`
  background: transparent;
  border: none;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(168, 85, 247, 0.1);
  }
`;

const DaysInput = styled.input`
  width: 100%;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  padding: 12px 16px;
  margin-bottom: 16px;
  
  &:focus {
    outline: none;
    border-color: rgba(168, 85, 247, 0.6);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const LockButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #a855f7 0%, #9333ea 100%);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 14px 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(168, 85, 247, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: rgba(80, 80, 90, 0.6);
    cursor: not-allowed;
    transform: none;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 20px;
  cursor: pointer;
  
  &:hover {
    color: #ffffff;
  }
`;

const TokenCloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.3);
  border: none;
  color: #ffffff;
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(0, 0, 0, 0.5);
  }
`;

const SearchInput = styled.input`
  width: 100%;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  padding: 12px 16px;
  margin-bottom: 12px;
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const TokenList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 16px;
`;

const SelectedTokenContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
`;

const ErrorMessage = styled.div`
  color: rgba(239, 68, 68, 0.9);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: transparent;
  border-radius: 8px;
`;

const SuccessMessage = styled.div`
  color: rgba(34, 197, 94, 0.9);
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  margin-bottom: 12px;
  padding: 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  line-height: 1.5;
`;

const TxLink = styled.a`
  color: rgba(168, 85, 247, 0.9);
  text-decoration: underline;
  cursor: pointer;
  
  &:hover {
    color: rgba(168, 85, 247, 1);
  }
`;

interface QuickLockModalProps {
  isOpen: boolean;
  onClose: () => void;
  userTokens: TokenData[];
  onLockCreated: () => void;
}

const LOCKER_CONTRACT_ADDRESS = '0xd2C5947A6777500D9b7ad55a4D48AeF855AE6aBA';

const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
  'function name() view returns (string)'
];

const LOCKER_ABI = [
  'function newTokenLock(address token, uint256 amount, uint256 lockDays) returns (uint256)',
  'function withdrawTokenLock(uint256 lockId) returns (bool)',
  'function getUserLockIds(address user) view returns (uint256[])',
  'function getUserLockCount(address user) view returns (uint256)',
  'function getLockInfo(address user, uint256 lockId) view returns (address tokenAddress, uint256 tokenAmount, uint256 lockDays, uint256 daysRemaining, bool isUnlocked, bool withdrawn)',
  'function getActiveLocks(address user, address token) view returns (uint256[])'
];

const QuickLockModal: React.FC<QuickLockModalProps> = ({ 
  isOpen, 
  onClose, 
  userTokens, 
  onLockCreated 
}) => {
  const { address, isConnected } = useAccount();
  const [selectedToken, setSelectedToken] = useState<TokenData | null>(null);
  const [amount, setAmount] = useState('');
  const [lockDays, setLockDays] = useState('');
  const [isLocking, setIsLocking] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [txHash, setTxHash] = useState('');
  const [tokenBalance, setTokenBalance] = useState('0');
  const [allowance, setAllowance] = useState('0');
  const [activeLocks, setActiveLocks] = useState<Array<{ lockId: bigint; amount: bigint; lockDays: bigint; daysRemaining: bigint; isUnlocked: boolean; withdrawn: boolean; }>>([]);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawingId, setWithdrawingId] = useState<bigint | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Reset state when modal closes (so it's fresh on next open)
  useEffect(() => {
    if (!isOpen) {
      setSelectedToken(null);
      setAmount('');
      setLockDays('');
      setError('');
      setSuccess('');
      setTxHash('');
      setTokenBalance('0');
      setAllowance('0');
      setActiveLocks([]);
      setSearchQuery('');
      setIsWithdrawing(false);
      setWithdrawingId(null);
    }
  }, [isOpen]);

  // Filter tokens based on search query
  const filteredTokens = userTokens.filter(token => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      token.name?.toLowerCase().includes(query) ||
      token.symbol?.toLowerCase().includes(query) ||
      token.contract_address?.toLowerCase().includes(query)
    );
  });

  // Fetch balance and allowance when token is selected
  useEffect(() => {
    if (selectedToken && address && isOpen) {
      fetchTokenData();
      fetchActiveLocks();
    }
  }, [selectedToken, address, isOpen]);

  const fetchTokenData = async () => {
    if (!selectedToken || !address) return;

    try {
      if (selectedToken.contract_address === '') {
        // Native ETH
        const balance = await executeWithRpcFailover(async (provider) => {
          return await provider.getBalance(address);
        });
        setTokenBalance(ethers.formatEther(balance));
        setAllowance('0');
      } else {
        // ERC-20 token
        const contract = new ethers.Contract(selectedToken.contract_address, ERC20_ABI, await executeWithRpcFailover(async (provider) => provider));
        
        const [balance, allowanceAmount] = await Promise.all([
          contract.balanceOf(address),
          contract.allowance(address, LOCKER_CONTRACT_ADDRESS)
        ]);

        const decimals = await contract.decimals();
        setTokenBalance(ethers.formatUnits(balance, decimals));
        setAllowance(ethers.formatUnits(allowanceAmount, decimals));
      }
    } catch (err) {
      console.error('Error fetching token data:', err);
      setError('Failed to fetch token balance');
    }
  };

  const fetchActiveLocks = async () => {
    if (!selectedToken || !address) return;
    
    try {
      const locker = new ethers.Contract(LOCKER_CONTRACT_ADDRESS, LOCKER_ABI, await executeWithRpcFailover(async (provider) => provider));
      const lockIds = await locker.getUserLockIds(address);
      
      const locks: Array<{ lockId: bigint; amount: bigint; lockDays: bigint; daysRemaining: bigint; isUnlocked: boolean; withdrawn: boolean; }> = [];
      
      for (const lockId of lockIds) {
        const info = await locker.getLockInfo(address, lockId);
        // Check if this lock is for the selected token
        if (info.tokenAddress.toLowerCase() === selectedToken.contract_address.toLowerCase()) {
          locks.push({
            lockId,
            amount: info.tokenAmount,
            lockDays: info.lockDays,
            daysRemaining: info.daysRemaining,
            isUnlocked: info.isUnlocked,
            withdrawn: info.withdrawn
          });
        }
      }
      
      setActiveLocks(locks);
    } catch (err) {
      console.error('Error fetching locks:', err);
    }
  };

  const handleWithdraw = async (lockId: bigint) => {
    if (!window.ethereum || !address) return;
    
    setIsWithdrawing(true);
    setWithdrawingId(lockId);
    
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const locker = new ethers.Contract(LOCKER_CONTRACT_ADDRESS, LOCKER_ABI, signer);
      
      const tx = await locker.withdrawTokenLock(lockId);
      await tx.wait();
      
      alert('Lock withdrawn successfully!');
      fetchActiveLocks(); // Refresh the locks list
    } catch (err: any) {
      if (err?.code === 4001) {
        alert('User rejected');
      } else {
        alert('Withdraw failed. Please try again.');
      }
    } finally {
      setIsWithdrawing(false);
      setWithdrawingId(null);
    }
  };

  const handleTokenSelect = (token: TokenData) => {
    setSelectedToken(token);
    setAmount('');
    setError('');
  };

  const handleClearToken = () => {
    setSelectedToken(null);
    setAmount('');
    setLockDays('');
    setError('');
    setSuccess('');
    setTxHash('');
  };

  const handlePercentClick = (percent: number) => {
    if (!tokenBalance) return;
    
    const balance = parseFloat(tokenBalance);
    const amountToLock = Math.floor((balance * percent) / 100);
    setAmount(amountToLock.toString());
  };

  const handleApprove = async () => {
    if (!selectedToken || !address) return;

    setIsApproving(true);
    setError('');

    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const contract = new ethers.Contract(selectedToken.contract_address, ERC20_ABI, signer);

      const tx = await contract.approve(LOCKER_CONTRACT_ADDRESS, ethers.MaxUint256);
      await tx.wait();

      // Refresh allowance
      await fetchTokenData();
    } catch (err: any) {
      console.error('Approve failed:', err);
      if (err?.code === 4001) {
        setError('Transaction rejected by user');
      } else {
        setError('Approval failed. Please try again.');
      }
    } finally {
      setIsApproving(false);
    }
  };

  const handleLock = async () => {
    if (!selectedToken || !address || !amount || !lockDays) return;

    setIsLocking(true);
    setError('');

    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const lockerContract = new ethers.Contract(LOCKER_CONTRACT_ADDRESS, LOCKER_ABI, signer);

      let tx;
      if (selectedToken.contract_address === '') {
        // Native ETH - would need to wrap to WETH first
        setError('ETH locking not supported. Please use WETH instead.');
        return;
      } else {
        // ERC-20 token
        const tokenContract = new ethers.Contract(selectedToken.contract_address, ERC20_ABI, signer);
        const decimals = await tokenContract.decimals();
        
        tx = await lockerContract.newTokenLock(
          selectedToken.contract_address,
          ethers.parseUnits(amount, decimals),
          parseInt(lockDays)
        );
      }

      const receipt = await tx.wait();
      
      // Show success message
      setSuccess(`Successfully locked ${amount} ${selectedToken.symbol} for ${lockDays} days!`);
      setTxHash(receipt.hash);
      
      // Reset form but keep success message
      setAmount('');
      setLockDays('');
      
      // Refresh token data and locks
      await fetchTokenData();
      await fetchActiveLocks();
      
      // Notify parent component
      onLockCreated();
    } catch (err: any) {
      console.error('Lock failed:', err);
      if (err?.code === 4001) {
        setError('Transaction rejected by user');
      } else {
        setError('Lock creation failed. Please try again.');
      }
    } finally {
      setIsLocking(false);
    }
  };

  const handleClose = () => {
    setSelectedToken(null);
    setAmount('');
    setLockDays('');
    setError('');
    setSuccess('');
    setTxHash('');
    setTokenBalance('0');
    setAllowance('0');
    onClose();
  };

  const needsApproval = selectedToken && amount && parseFloat(amount) > 0 && parseFloat(allowance) < parseFloat(amount);
  const canLock = selectedToken && amount && lockDays && parseFloat(amount) > 0 && parseInt(lockDays) > 0 && !needsApproval;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={{
        overlay: { 
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 10001,
        },
        content: {
          position: 'relative',
          inset: 'auto',
          background: 'transparent',
          border: 'none',
          padding: 0,
          maxHeight: '90vh',
          maxWidth: '90%',
          width: '100%',
        },
      }}
    >
      <ModalContainer>
        <ModalContent>
        <CloseButton onClick={handleClose}>×</CloseButton>
        
        <Title>Lock Tokens</Title>
        
        {!selectedToken ? (
          <>
            <SectionLabel>Select Token</SectionLabel>
            <SearchInput
              type="text"
              placeholder="Search by name, symbol, or address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <TokenList>
              {filteredTokens.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  color: 'rgba(255, 255, 255, 0.5)', 
                  padding: '20px',
                  fontSize: '14px'
                }}>
                  No tokens found
                </div>
              ) : (
                filteredTokens.map((token) => (
                  <TokenDisplay 
                    key={token.contract_address} 
                    onClick={() => handleTokenSelect(token)}
                    style={{ cursor: 'pointer', marginBottom: '8px' }}
                  >
                    {token.logo_url && (
                      <TokenLogo 
                        src={token.logo_url} 
                        alt={token.symbol}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/default-token.png';
                        }}
                      />
                    )}
                    <TokenInfo>
                      <TokenSymbol>{token.symbol}</TokenSymbol>
                      <TokenName>{token.name}</TokenName>
                    </TokenInfo>
                  </TokenDisplay>
                ))
              )}
            </TokenList>
          </>
        ) : (
          <>
            <SectionLabel>Selected Token</SectionLabel>
            <TokenDisplay>
              {selectedToken.logo_url && (
                <TokenLogo 
                  src={selectedToken.logo_url} 
                  alt={selectedToken.symbol}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/default-token.png';
                  }}
                />
              )}
              <TokenInfo>
                <TokenSymbol>{selectedToken.symbol}</TokenSymbol>
                <TokenName>{selectedToken.name}</TokenName>
              </TokenInfo>
            </TokenDisplay>

            <BalanceRow>
              <BalanceText>Balance: {parseFloat(tokenBalance).toLocaleString()} {selectedToken.symbol}</BalanceText>
            </BalanceRow>

            <AmountRow>
              <AmountInput
                type="number"
                placeholder="0.0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                step="1"
                min="0"
              />
            </AmountRow>

            <PercentButtons>
              <PercentButton onClick={() => handlePercentClick(25)}>25%</PercentButton>
              <PercentButton onClick={() => handlePercentClick(50)}>50%</PercentButton>
              <PercentButton onClick={() => handlePercentClick(75)}>75%</PercentButton>
              <PercentButton onClick={() => handlePercentClick(100)}>Max</PercentButton>
            </PercentButtons>

            <SectionLabel>Lock Duration (Days)</SectionLabel>
            <DaysInput
              type="number"
              placeholder="Enter days"
              value={lockDays}
              onChange={(e) => setLockDays(e.target.value)}
              min="1"
              step="1"
            />

            {/* Active Locks Display */}
            {activeLocks.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <SectionLabel>Active Locks</SectionLabel>
                <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '12px' }}>
                  {activeLocks.map((lock) => {
                    const isClaimable = lock.isUnlocked && !lock.withdrawn;
                    return (
                      <div key={lock.lockId.toString()} style={{ 
                        background: 'transparent', 
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '8px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Lock #{lock.lockId.toString()}</span>
                          <span style={{ fontSize: '12px', color: isClaimable ? '#22c55e' : 'rgba(255,255,255,0.7)' }}>
                            {lock.withdrawn ? 'Withdrawn' : isClaimable ? 'Unlocked' : 'Locked'}
                          </span>
                        </div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>
                          Amount: {ethers.formatEther(lock.amount)} {selectedToken.symbol}
                        </div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>
                          {lock.isUnlocked ? 'Ready to withdraw' : `${lock.daysRemaining.toString()} days remaining`}
                        </div>
                        {isClaimable && (
                          <LockButton 
                            onClick={() => handleWithdraw(lock.lockId)} 
                            disabled={isWithdrawing}
                            style={{ fontSize: '12px', padding: '6px 12px', minHeight: 'auto' }}
                          >
                            {isWithdrawing && withdrawingId === lock.lockId ? 'Withdrawing...' : 'Withdraw'}
                          </LockButton>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            {success && (
              <SuccessMessage>
                ✓ {success}
                <br />
                <TxLink 
                  href={`https://explorer.inkonchain.com/tx/${txHash}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  View transaction
                </TxLink>
              </SuccessMessage>
            )}

            {needsApproval && (
              <LockButton onClick={handleApprove} disabled={isApproving}>
                {isApproving ? 'Approving...' : 'Approve Token'}
              </LockButton>
            )}

            {!needsApproval && (
              <LockButton onClick={handleLock} disabled={!canLock || isLocking || !!success}>
                {isLocking ? 'Locking...' : success ? 'Locked Successfully' : `Lock ${selectedToken.symbol}`}
              </LockButton>
            )}
          </>
        )}
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default QuickLockModal;
